import db, { auth } from '../firebaseConfig';
import { getCollectionName, VALID_COUNTRY_IDS } from './countryConfig';

// Include every input that can affect an identity. Do not download inline photos,
// styleDetails images, history or embeddings just to check uniqueness.
export const IDENTITY_FIELDS = ['itemNumber', 'barcode', 'rawBarcode', 'styleBarcodes', 'rawStyleBarcodes', 'name'];
export const identityQuery = collectionId => ({ structuredQuery: {
  from: [{ collectionId }], select: { fields: IDENTITY_FIELDS.map(fieldPath => ({ fieldPath })) },
} });
const readTimeout = () => Object.assign(new Error('讀取檢查逾時，這次尚未進入寫入步驟。請確認網路後重試；輸入與照片仍保留，重試會先確認先前結果。'), { code: 'product-read-timeout' });

// Apply deadlines only before writes. A timed-out SDK read may finish later, but
// its caller has already rejected and cannot continue into a write transaction.
export const readWithDeadline = async (read, timeoutMs = 30000) => {
  const controller = new AbortController();
  let timer;
  const deadline = new Promise((_, reject) => {
    timer = setTimeout(() => { controller.abort(); reject(readTimeout()); }, timeoutMs);
  });
  try { return await Promise.race([Promise.resolve().then(() => read(controller.signal)), deadline]); }
  catch (error) { if (controller.signal.aborted) throw readTimeout(); throw error; }
  finally { clearTimeout(timer); }
};

const decode = value => {
  if ('stringValue' in value) return value.stringValue;
  if ('integerValue' in value) return Number(value.integerValue);
  if ('doubleValue' in value) return Number(value.doubleValue);
  if ('booleanValue' in value) return value.booleanValue;
  if ('nullValue' in value) return null;
  if ('arrayValue' in value) return (value.arrayValue.values || []).map(decode);
  // Unexpected identity types must not silently turn into a clean duplicate check.
  throw new Error('商品識別欄位格式不支援，未新增商品。請先確認舊資料。');
};
export const decodeIdentityResponse = (rows, prefix) => {
  if (!Array.isArray(rows) || !rows.length) throw new Error('識別碼檢查回應不完整，未新增商品');
  return rows.flatMap(row => {
    if (row.error || row.skippedResults || row.done === false) throw new Error('識別碼檢查未完成，未新增商品');
    if (!row.document) {
      if (!row.readTime && row.done !== true) throw new Error('識別碼檢查回應不完整，未新增商品');
      return [];
    }
    const { name, fields = {} } = row.document;
    if (!name?.startsWith(prefix) || !name.slice(prefix.length) || name.slice(prefix.length).includes('/')) throw new Error('識別碼檢查回傳非預期商品，未新增商品');
    const product = Object.fromEntries(Object.entries(fields).map(([key, value]) => [key, decode(value)]));
    return [{ ...product, id: name.slice(prefix.length) }];
  });
};

export const readProductIdentities = async (country, actor) => {
  if (!VALID_COUNTRY_IDS.includes(country)) throw new Error('無效的國家');
  const user = auth.currentUser;
  if (!user || user.uid !== actor) throw new Error('登入狀態已變更，請重新登入');
  const projectId = db.app.options.projectId;
  const parent = `projects/${projectId}/databases/(default)/documents`;
  const collectionId = getCollectionName(country);
  return readWithDeadline(async signal => {
    // Use only the signed-in user's ID token: REST reads enforce the same rules.
    // No service account, new access, cached catalogue or fallback on read failure.
    const token = await user.getIdToken();
    if (signal.aborted) throw readTimeout();
    if (auth.currentUser?.uid !== actor) throw new Error('登入狀態已變更，請重新登入');
    const response = await fetch(`https://firestore.googleapis.com/v1/${parent}:runQuery`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(identityQuery(collectionId)), signal,
    });
    if (!response.ok) throw new Error(`無法完成識別碼檢查（${response.status}），未新增商品；請確認網路與登入狀態後重試。`);
    const products = decodeIdentityResponse(await response.json(), `${parent}/${collectionId}/`);
    if (auth.currentUser?.uid !== actor) throw new Error('登入狀態已變更，請重新登入');
    return products;
  });
};
